import React, { createContext, useCallback, useEffect, useState } from 'react';
import useSWR, { useSWRConfig, SWRConfiguration } from 'swr';
import { useEnvironment } from 'context/EnvironmentContext/EnvironmentContext';
import client, { UserAuth, isUserAuth, Options } from 'utils/apiClient';
import { emailFromId, expiresIn, refresh } from 'utils/apiUser';
import { storageTokenKey } from 'utils/storage';

interface AuthProviderProps {
  children: React.ReactNode;
}

interface State {
  tokens: UserAuth | undefined;
  setTokens: (data?: UserAuth) => void;
  email: string | undefined;
}

const AuthContext = createContext<State | undefined>(undefined);

export function AuthProvider(props: AuthProviderProps) {
  let initialState: UserAuth | undefined;

  const existingTokens = window.localStorage.getItem(storageTokenKey);

  if (existingTokens) {
    const parsedTokens = JSON.parse(existingTokens);

    if (isUserAuth(parsedTokens)) {
      initialState = parsedTokens;
    }
  }

  const [tokens, setTokens] = useState<UserAuth | undefined>(initialState);

  const setData = (tokens?: UserAuth) => {
    if (tokens) {
      window.localStorage.setItem(storageTokenKey, JSON.stringify(tokens));
    } else {
      window.localStorage.removeItem(storageTokenKey);
    }

    setTokens(tokens);
  };

  const email = tokens ? emailFromId(tokens.id_token) : undefined;
  const value = { tokens, setTokens: setData, email };
  return <AuthContext.Provider value={value} {...props} />;
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error(`useAuth must be used within a AuthProvider`);
  }
  return context;
}

/*
 * Returns a `client` and `mutate` function with the relevant auth tokens applied.
 * It will also update auth tokens when necessary using the refresh token.
 */
export function useClient() {
  const { setTokens } = useAuth();
  const tokens = useFreshTokens();
  const { mutate } = useSWRConfig();

  const clientFn = useCallback(
    (endpoint: RequestInfo, config: Options = {}) =>
      client(endpoint, { ...config, tokens }, setTokens),
    [tokens, setTokens]
  );

  const mutateFn = useCallback(
    (endpoint: string) => mutate([endpoint, tokens]),
    [mutate, tokens]
  );

  return {
    client: clientFn,
    mutate: mutateFn,
    tokens,
  };
}

/*
 * A wrapper around useSWR with auth tokens and fetcher applied.
 */
export function useClientSWR<Data = any, Error = any>(
  endpoint: string | null,
  options?: SWRConfiguration
) {
  const { client, tokens } = useClient();
  const key: [string, UserAuth] | null =
    endpoint && tokens ? [endpoint, tokens] : null;
  return useSWR<Data, Error>(key, ([url]) => client(url), options);
}

function useFreshTokens() {
  const { tokens, setTokens } = useAuth();
  const { environment } = useEnvironment();

  useEffect(() => {
    if (tokens) {
      if (expiresIn(tokens.access_token, 0)) {
        setTokens();
        return;
      }

      if (expiresIn(tokens.access_token, 5)) {
        refresh(tokens.refresh_token, tokens.id_token, environment)
          .then((response) => {
            const freshTokens = {
              ...response,
              refresh_token: tokens.refresh_token,
              initiate_mfa: tokens.initiate_mfa,
            };
            setTokens(freshTokens);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }, [environment, tokens, setTokens]);

  return tokens;
}
